import { useEffect, useMemo, useRef, useState } from 'react';
import { useCubeApi } from '../use_cube_api/use_cube_api.hook';
const DEFAULT_LIMIT = 50000;
export const fetchAllCubePages = async ({ cubeApi, query, signal, loadOptions, limit = DEFAULT_LIMIT, maxRetries = 3, retryDelayBase = 1000, onPageFetched, }) => {
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fetchPageWithRetry = async (pageQuery, pageNumber) => {
        let lastError = null;
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                return await cubeApi.load(pageQuery, { ...loadOptions, cache: 'must-revalidate', signal });
            }
            catch (err) {
                if (err instanceof Error && err.name === 'AbortError') {
                    throw err;
                }
                lastError = err instanceof Error ? err : new Error(`Failed to fetch page ${pageNumber}`);
                if (attempt < maxRetries) {
                    const delay = retryDelayBase * Math.pow(2, attempt - 1);
                    await sleep(delay);
                }
            }
        }
        throw lastError || new Error(`Failed to fetch page ${pageNumber} after ${maxRetries} attempts`);
    };
    const firstPageQuery = {
        ...query,
        total: true,
        offset: 0,
        limit: query.limit || limit,
    };
    const firstResult = await fetchPageWithRetry(firstPageQuery, 1);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loadResponse = firstResult.loadResponse?.results?.[0];
    if (!loadResponse) {
        return {
            resultSets: [],
            totalRecords: 0,
            totalPages: 0,
            fetchedPages: 0,
            actualLimit: query.limit || limit,
        };
    }
    const total = loadResponse.total || 0;
    const actualLimit = loadResponse.query?.limit || query.limit || limit;
    const totalPages = Math.ceil(total / actualLimit);
    const allResultSets = [firstResult];
    let fetchedPages = 1;
    onPageFetched?.(fetchedPages, totalPages, total);
    for (let page = 1; page < totalPages; page++) {
        const pageQuery = {
            ...query,
            total: true,
            offset: actualLimit * page,
            limit: actualLimit,
        };
        const pageResult = await fetchPageWithRetry(pageQuery, page + 1);
        allResultSets.push(pageResult);
        fetchedPages = page + 1;
        onPageFetched?.(fetchedPages, totalPages, total);
    }
    return {
        resultSets: allResultSets,
        totalRecords: total,
        totalPages,
        fetchedPages,
        actualLimit,
    };
};
export const usePaginatedCubeQuery = (query, options = {}) => {
    const { cubeApi, freshCubeApi } = useCubeApi({ disablePreAggs: options.disablePreAggs });
    // During a manual refresh, load through the always-fresh client. It is read via a ref so it
    // stays out of the effect deps — that way reverting `forceFresh` after refresh can't trigger a
    // redundant pre-agg fetch that would overwrite the freshly loaded data.
    const activeApi = options.forceFresh && freshCubeApi ? freshCubeApi : cubeApi;
    const activeApiRef = useRef(activeApi);
    activeApiRef.current = activeApi;
    const [resultSets, setResultSets] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [paginationInfo, setPaginationInfo] = useState({
        totalRecords: 0,
        totalPages: 0,
        fetchedPages: 0,
    });
    // Serialize query to a stable string to prevent infinite re-renders
    // when the query object reference changes but the content is the same
    const serializedQuery = useMemo(() => (query ? JSON.stringify(query) : ''), [query]);
    useEffect(() => {
        if (!query || !cubeApi || options.skip) {
            return;
        }
        setIsLoading(true);
        setError(null);
        options.onQueryExecuted?.();
        const controller = new AbortController();
        const { signal } = controller;
        let isEffectActive = true;
        const fetchAllPages = async () => {
            try {
                const { resultSets } = await fetchAllCubePages({
                    cubeApi: activeApiRef.current,
                    query,
                    signal,
                    limit: DEFAULT_LIMIT,
                    onPageFetched: (fetchedPages, totalPages, totalRecords) => {
                        if (isEffectActive) {
                            setPaginationInfo({
                                totalRecords,
                                totalPages,
                                fetchedPages,
                            });
                        }
                    },
                });
                if (isEffectActive) {
                    setResultSets(resultSets);
                    setIsLoading(false);
                }
            }
            catch (err) {
                if (err instanceof Error && err.name === 'AbortError') {
                    return;
                }
                if (isEffectActive) {
                    setError(err instanceof Error ? err : new Error('Failed to fetch paginated data'));
                    setIsLoading(false);
                }
            }
        };
        fetchAllPages();
        return () => {
            isEffectActive = false;
            controller.abort();
        };
    }, [serializedQuery, cubeApi, options.skip, options.refreshNonce]);
    return {
        resultSets,
        isLoading,
        error,
        paginationInfo,
    };
};
